import { Entity } from "ecsy";
import { Physics, Transform, Sync, Health, AimOrbit, AimForPosition, Targeting, ElementInfo } from "./components";
import { claimVec2, passVec2 } from "./box2dUtils";
import { getEntityById, getId } from "./entityFactory";
import { vec2 } from "@tlaukkan/tsm"
import { vecToStr } from "./utils";
import { CreationReceipt, SyncEntityState, SyncMessageType, TargetChangeReceipt, Tiebreaker } from "./messageTypes";
import { Composition } from "./composition";
import { Game } from "./game";
import { GameElement } from "./gameElementTypes";

export type ArrayVec2 = [number, number];

let lastEntitySyncId = 1;

export function generateSyncId() {
	if (!process.env.is_server) throw new Error('Attempted to generate syncID on client');
	return lastEntitySyncId++;
}

export function resetSyncIDs(startingAt: number = 1) {
	lastEntitySyncId = startingAt;
}

export function serializeEntityState(obj: Entity) {
	const physics = obj.getComponent(Physics);
	const transform = obj.getComponent(Transform);
	const syncInfo = obj.getComponent(Sync);

	const id = syncInfo.id;
	const position = claimVec2(physics.body.GetPosition());
	const rotation = physics.body.GetAngle();
	const direction = transform.direction;
	const linearVelocity = claimVec2(physics.body.GetLinearVelocity());
	const angularVelocity = physics.body.GetAngularVelocity();

	const serialized: SyncEntityState = {
		id,
		position: position.xy,
		angle: rotation,
		direction: direction.xy,
		linearVelocity: linearVelocity.xy,
		angularVelocity
	};

	if (obj.hasComponent(Health)) {
		serialized.hp = obj.getComponent(Health).hp
	}

	if (obj.hasComponent(AimOrbit)) {
		serialized.orbitTarget = obj.getComponent(AimOrbit).target.xy;
	}

	if (obj.hasComponent(AimForPosition)) {
		serialized.straightTarget = obj.getComponent(AimForPosition).target.xy;
	}

	return serialized;
}

export function deserializeEntityState(obj: Entity, serialized: SyncEntityState) {
	const physics = obj.getMutableComponent(Physics);
	const transform = obj.getMutableComponent(Transform);

	transform.direction = new vec2(serialized.direction);
	/**Kinda ugly, but if an object is created on the same frame as we have the
	 * sync state for it, it won't have a physics body because the physics system hasn't
	 * run on it yet. In theory we shouldn't need to sync states on an objects first frame 
	 * of life, so, a better solution here might be to avoid that in serverSyncSystem.
	 * Other problems of this sort could arise that won't be solved with this cludge.
	 */
	if (physics.body) {
		if (serialized.position[0] === null || serialized.position[1] === null) throw new Error('FOUND NULL POSITION');

		//Test code for synchronization drift
		//logSyncDifference(physics, serialized);

		physics.body.SetTransform(passVec2(new vec2(serialized.position)), serialized.angle);
		physics.body.SetLinearVelocity(passVec2(new vec2(serialized.linearVelocity)));
		physics.body.SetAngularVelocity(serialized.angularVelocity);
	}

	//Health
	if (serialized.hp) {
		obj.getComponent(Health).hp = serialized.hp; //not getMutableComponent on purpose, don't want to trigger changes
	}

	//Orbiter
	if (serialized.orbitTarget) {
		obj.getMutableComponent(AimOrbit).target.xy = serialized.orbitTarget;
	}

	if (serialized.straightTarget) {
		obj.getMutableComponent(AimForPosition).target.xy = serialized.straightTarget;
	}

}

export function serializeTargetList(id: number, entities: Entity[], specIndex: number): TargetChangeReceipt {
	const serialized = entities.map(entity => getId(entity) || -1);
	const receiverType = getEntityById(id)?.getComponent(ElementInfo)?.type ?? GameElement.INTEREST; //a game element that isn't ever used 
	return {
		id,
		specIndex,
		result: serialized,
		receiverType: receiverType
	}
}

export function deserializeTargetList(change: TargetChangeReceipt) {

	return change.result.map(id => getEntityById(id)).filter(item => item !== null) as Entity[];
}

type SyncLogs = { id: number, pos: vec2, angle: number, linearV: vec2, anglularV: number };
const syncLogs: Array<SyncLogs> = [];
let objectsSynced = 0;

function logSyncDifference(local: Physics, remote: SyncEntityState) {
	objectsSynced++;
	const pos = claimVec2(local.body.GetPosition()).subtract(new vec2(remote.position));
	const angle = local.body.GetAngle() - remote.angle;
	const linearV = claimVec2(local.body.GetLinearVelocity()).subtract(new vec2(remote.linearVelocity));
	const anglularV = local.body.GetAngularVelocity() - remote.angularVelocity;

	const eps = Math.pow(2, -7);
	if (!(pos.length() < eps && linearV.length() < eps && anglularV < eps && angle < eps)) {
		syncLogs.push({ id: remote.id, pos, angle, linearV, anglularV })
	}
}

export function reportSyncDifference() {

	const sum = (key: keyof SyncLogs) => {
		return syncLogs.map(entry => entry[key]).reduce((curr, prev) => {
			if (prev instanceof vec2) {
				return prev.copy().add(curr as vec2);
			}
			else {
				return prev + (curr as number);
			}
		});
	}

	const avg = (key: keyof SyncLogs) => {
		if (syncLogs.length === 0) return 0;
		if (syncLogs.length === 1) return syncLogs[0];
		else {
			const total = sum(key);
			if (total instanceof vec2) return total.divide(new vec2([syncLogs.length, syncLogs.length]));
			else return total / syncLogs.length;
		}
	}

	const max = (key: keyof SyncLogs) => {
		if (syncLogs.length === 0) return 0;
		if (syncLogs.length === 1) return syncLogs[0];
		else {
			return syncLogs.map(entry => entry[key]).reduce((curr, prev) => {
				if (prev instanceof vec2) {
					return prev.length() > (curr as vec2).length() ? prev : curr;
				}
				else {
					return prev > (curr as number) ? prev : curr;
				}
			});
		}
	}

	let output = `Sync Log: Objects so far: ${objectsSynced}, Number desynced: ${syncLogs.length}`;
	console.log()
	for (const key of ['angle', 'anglularV']) {
		const maxiumum = max(key as any);
		const average = avg(key as any);
		output += `\n Value: ${key} Maximum: ${maxiumum}, Avg: ${average}`;
	}
	for (const key of ['pos', 'linearV']) {
		const maxiumum = max(key as any);
		const average = avg(key as any);
		output += `\n Value: ${key} Maximum: ${vecToStr(maxiumum as vec2)}, Avg: ${vecToStr(average as vec2)}`;
	}
	console.log(output)
}

export function serializeTiebreaker(tiebreakerRound: number, cellsAdded: Composition, cellsIncreased: Composition, owningPlayerId: number) {
	const newCells = cellsAdded.map(cell => {
		return {
			type: cell.type,
			playerId: owningPlayerId,
			position: cell.position,
			rotation: cell.rotation,
			id: cell.id,
			entityParameters: cell.entityParameters
		} as CreationReceipt
	});

	return {
		messageType: SyncMessageType.Tiebreaker as SyncMessageType.Tiebreaker, //why did I need this cast??
		tiebreakerRound,
		cellsAdded: newCells,
		cellsIncreased: cellsIncreased.map(cell => cell.id),
		owningPlayerId,
		stepNumber: getStepNumber(),
		noQueue: true //messages sent from state during simulation can block message buffer because clientSyncSystem runs before stateSystem
	} as Tiebreaker;
}

export function deserializeTiebreaker(tiebreaker: Tiebreaker, game: Game) {
	//Apply the growth to existing entites
	const cellsIncreased = tiebreaker.cellsIncreased.map(cellId => game.getCompElementById(cellId))

	//Create new cells (unsynced, client will repeat this on getting tiebreaker message)
	const cellsAdded = tiebreaker.cellsAdded.map(cellId => {
		return {
			type: cellId.type,
			position: cellId.position,
			rotation: cellId.rotation,
			id: cellId.id,
			combined: 0,
			entityParameters: cellId.entityParameters
		}
	});

	return { cellsIncreased, cellsAdded, owningPlayerId: tiebreaker.owningPlayerId }
}